import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Table, Button, Input, Space, Popconfirm, Typography, message, Select, Divider, Tooltip } from 'antd';
// Replace InputRef with TextAreaRef for focusing the TextArea
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { useDispatch } from 'react-redux';
import { $api } from '../../../services/service';
import { setLoading } from '../../../redux/authSlice';

const { TextArea } = Input;

// Types
export type EvaluationDto = {
    id: string;
    questionId?: string | null;
    questionText?: string | null;
    name: string;
    description?: string | null;
    type: number; // Enum.EvaluationType
    score: number;
    parentId?: string | null;
    parentName?: string | null;
    children?: EvaluationDto[] | null;
};

interface Props {
    show: boolean;
    onHide: () => void;
    questionId: string;
    questionText?: string;
    subjectId?: string;
    questionSuiteId?: string;
}

// Helpers
function cloneTree<T extends { id: string; children?: T[] | null }>(nodes: T[] | null | undefined): T[] {
    if (!nodes) return [];
    return nodes.map(n => ({ ...n, children: n.children ? cloneTree(n.children) : [] }));
}

function findNode(nodes: EvaluationDto[] | null | undefined, id: string): EvaluationDto | null {
    if (!nodes) return null;
    for (const n of nodes) {
        if (n.id === id) return n;
        const child = findNode(n.children || [], id);
        if (child) return child;
    }
    return null;
}

// Convert number to Roman numerals (supports up to large values)
function toRoman(num: number): string {
    if (num <= 0) return '';
    const map: { value: number; symbol: string }[] = [
        { value: 1000, symbol: 'M' },
        { value: 900, symbol: 'CM' },
        { value: 500, symbol: 'D' },
        { value: 400, symbol: 'CD' },
        { value: 100, symbol: 'C' },
        { value: 90, symbol: 'XC' },
        { value: 50, symbol: 'L' },
        { value: 40, symbol: 'XL' },
        { value: 10, symbol: 'X' },
        { value: 9, symbol: 'IX' },
        { value: 5, symbol: 'V' },
        { value: 4, symbol: 'IV' },
        { value: 1, symbol: 'I' },
    ];
    let result = '';
    let n = Math.floor(num);
    for (const { value, symbol } of map) {
        while (n >= value) {
            result += symbol;
            n -= value;
        }
        if (n === 0) break;
    }
    return result;
}

// Build STT map: root level I, II, III...; children 1, 2, 3 ...; deeper levels are not numbered
function makeIndexMap(nodes: EvaluationDto[] = []): Record<string, string> {
    const map: Record<string, string> = {};
    nodes.forEach((root, i) => {
        map[root.id] = toRoman(i + 1);
        if (root.children && root.children.length) {
            root.children.forEach((child, j) => {
                map[child.id] = String(j + 1);
            });
        }
    });
    return map;
}

function collectAllKeys(nodes: EvaluationDto[] | null | undefined): string[] {
    if (!nodes) return [];
    const keys: string[] = [];
    const dfs = (arr: EvaluationDto[] | null | undefined) => {
        if (!arr) return;
        for (const n of arr) {
            keys.push(n.id);
            if (n.children && n.children.length) dfs(n.children);
        }
    };
    dfs(nodes);
    return keys;
}

// Sum of leaf scores (nodes without children)
function sumLeafScores(nodes: EvaluationDto[] | null | undefined): number {
    if (!nodes) return 0;
    let total = 0;
    const dfs = (arr: EvaluationDto[] | null | undefined) => {
        if (!arr) return;
        for (const n of arr) {
            const hasChildren = !!(n.children && n.children.length);
            if (hasChildren) {
                dfs(n.children);
            } else {
                total += Number(n.score) || 0;
            }
        }
    };
    dfs(nodes);
    // Fix floating point precision error by rounding to 2 decimal places
    return Math.round(total * 100) / 100;
}

// Escapes a string for use in RegExp
function escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Strip any leading STT like "I. ", "IV. ", "1. ", "2) " from a name (robust fallback)
function stripGenericSttPrefix(name: string): string {
    if (!name) return '';
    // Trim left first
    let s = name.replace(/^\s+/, '');
    // Pattern: Roman numerals or digits followed by '.' or ')' and optional spaces
    const re = /^(?:(?=[MDCLXVI]+[.)]\s*)[MDCLXVI]+|\d+)[.)]\s*/i;
    s = s.replace(re, '');
    return s.trimStart();
}

// Strip expected STT label (from map) if present; fallback to generic
function stripSttByLabel(name: string, label?: string): string {
    if (!name) return '';
    if (label) {
        // Remove optional leading spaces, the exact label, followed by '.' or ')', then optional spaces
        const re = new RegExp('^\\s*' + escapeRegExp(label) + '[\\.)]\\s*', 'i');
        const replaced = name.replace(re, '');
        if (replaced !== name) return replaced.trimStart();
    }
    return stripGenericSttPrefix(name);
}

const PracticeQuestion__Evaluation: React.FC<Props> = ({ show, onHide, questionId, questionText, subjectId, questionSuiteId }) => {
    const dispatch = useDispatch();

    const [loading, setLocalLoading] = useState(false);
    const [list, setList] = useState<EvaluationDto[]>([]);

    // Expanded rows
    const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

    // Inline edit state
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isNew, setIsNew] = useState<boolean>(false); // new row flag
    const [editParentId, setEditParentId] = useState<string | null>(null); // for new child case
    const [formName, setFormName] = useState('');
    const [formScore, setFormScore] = useState<number>(0);
    const [formDesc, setFormDesc] = useState('');

    // Duplicate states
    const [questionSuites, setQuestionSuites] = useState<any[]>([]);
    const [selectedQuestionSuiteId, setSelectedQuestionSuiteId] = useState<string | undefined>(questionSuiteId);
    const [questions, setQuestions] = useState<any[]>([]);
    const [selectedSourceQuestionId, setSelectedSourceQuestionId] = useState<string | undefined>(undefined);
    const [loadingQuestionSuites, setLoadingQuestionSuites] = useState(false);
    const [loadingQuestions, setLoadingQuestions] = useState(false);
    const [duplicating, setDuplicating] = useState(false);

    // Ref to focus the Name input
    const nameInputRef = useRef<TextAreaRef>(null);

    useEffect(() => {
        if (show && subjectId) {
            loadQuestionSuites();
        }
    }, [show, subjectId]);

    useEffect(() => {
        if (show && selectedQuestionSuiteId) {
            loadQuestions(selectedQuestionSuiteId);
        }
    }, [show, selectedQuestionSuiteId]);

    const loadQuestionSuites = async () => {
        if (!subjectId) return;
        setLoadingQuestionSuites(true);
        try {
            const res: any = await $api.question.getQuestionSuite(subjectId, '');
            const items = res?.result?.items || [];
            setQuestionSuites(items);
        } catch (e: any) {
            let errMsg = 'Không thể tải danh sách bộ câu hỏi';
            if (typeof e?.response?.data?.errors?.exceptionMessage === 'string') {
                errMsg = e.response.data.errors.exceptionMessage;
            }
            message.error(errMsg);
        } finally {
            setLoadingQuestionSuites(false);
        }
    };

    const loadQuestions = async (qSuiteId: string) => {
        setLoadingQuestions(true);
        try {
            const res: any = await $api.question.getPagingQuestion({
                questionSuiteId: qSuiteId,
                level: 3
            });
            const items = res?.result?.items || [];
            const filteredItems = items.filter((q: any) => q.id !== questionId);
            setQuestions(filteredItems);
            setSelectedSourceQuestionId(undefined); // Reset selected question
        } catch (e: any) {
            let errMsg = 'Không thể tải danh sách câu hỏi';
            if (typeof e?.response?.data?.errors?.exceptionMessage === 'string') {
                errMsg = e.response.data.errors.exceptionMessage;
            }
            message.error(errMsg);
        } finally {
            setLoadingQuestions(false);
        }
    };

    const handleDuplicate = async () => {
        if (!selectedSourceQuestionId) {
            message.warning('Vui lòng chọn câu hỏi nguồn');
            return;
        }
        if (!questionId) {
            message.error('Không tìm thấy câu hỏi đích');
            return;
        }

        setDuplicating(true);
        try {
            await $api.evaluation.duplicate({
                questionIdSrc: selectedSourceQuestionId,
                questionIdDes: questionId
            });
            message.success('Nhân bản tiêu chí thành công');
            await reload();
            // Reset selections
            setSelectedQuestionSuiteId(questionSuiteId);
            setSelectedSourceQuestionId(undefined);
        } catch (e: any) {
            let errMsg = 'Nhân bản tiêu chí thất bại';
            if (typeof e?.response?.data?.errors?.exceptionMessage === 'string') {
                errMsg = e.response.data.errors.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e.response.data.errors.exceptionMessage.title;
            }
            message.error(errMsg);
        } finally {
            setDuplicating(false);
        }
    };

    const resetEdit = () => {
        setEditingId(null);
        setIsNew(false);
        setEditParentId(null);
        setFormName('');
        setFormScore(0);
        setFormDesc('');
    };

    const startEdit = (rec: EvaluationDto) => {
        setEditingId(rec.id);
        setIsNew(false);
        setEditParentId(rec.parentId ?? null);
        // Use display-only name (strip existing STT if any)
        setFormName(stripSttByLabel(rec.name || '', sttMap[rec.id]));
        setFormScore(Number(rec.score) || 0);
        setFormDesc(rec.description || '');
    };

    const startAddRoot = () => {
        // New top-level row (optimistically appended at the end)
        const tmpId = `tmp_${Date.now()}`;
        setEditingId(tmpId);
        setIsNew(true);
        setEditParentId(null);
        setFormName('');
        setFormScore(0);
        setFormDesc('');
        // Put a temporary node into view so user sees inline editors (append to end)
        setList(prev => [...prev, { id: tmpId, name: '', description: '', score: 0, type: 2, parentId: null, children: [] }]);
        // Expand all so the new item is visible
        setExpandedRowKeys(prev => Array.from(new Set([...(prev || []), tmpId])));
    };

    const startAddChild = (parent: EvaluationDto) => {
        // Only allow adding children to root level (one level of children maximum)
        if (parent.parentId) {
            message.error('Chỉ cho phép xuống 1 cấp con.');
            return;
        }
        const tmpId = `tmp_${Date.now()}`;
        const newChild: EvaluationDto = { id: tmpId, name: '', description: '', score: 0, type: 2, parentId: parent.id, children: [] } as any;
        // Insert temp child under parent (for inline UI) - append to end
        setList(prev => {
            const copy = cloneTree(prev) as EvaluationDto[];
            const p = findNode(copy, parent.id);
            if (p) {
                const arr = Array.isArray(p.children) ? [...(p.children as EvaluationDto[])] : [];
                p.children = [...arr, newChild];
            }
            return copy;
        });
        // Ensure parent expanded so the child row becomes visible
        setExpandedRowKeys(prev => Array.from(new Set([...(prev || []), parent.id])));
        setEditingId(tmpId);
        setIsNew(true);
        setEditParentId(parent.id);
        setFormName('');
        setFormScore(0);
        setFormDesc('');
    };

    const cancelEdit = () => {
        // If it's a temp new row, remove it from UI
        if (isNew && editingId) {
            const tmpId = editingId;
            setList(prev => {
                const removeTmp = (nodes: EvaluationDto[] | null | undefined): EvaluationDto[] => {
                    if (!nodes) return [];
                    return nodes
                        .filter(n => n.id !== tmpId)
                        .map(n => ({ ...n, children: removeTmp(n.children) }));
                };
                return removeTmp(prev);
            });
        }
        resetEdit();
    };

    const reload = async () => {
        setLocalLoading(true);
        try {
            const res: any = await $api.evaluation.getPaging({ questionId });
            const items: EvaluationDto[] = res?.result?.items ?? res?.result ?? [];
            setList(items || []);
            // Expand all by default when data loads
            setExpandedRowKeys(collectAllKeys(items));
        } catch (e: any) {
            let errMsg;
            const type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Đã có lỗi xảy ra khi tải tiêu chí';
            }
            message.error(errMsg);
        } finally {
            setLocalLoading(false);
        }
    };

    useEffect(() => {
        if (show && questionId) {
            reload();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, questionId]);

    // Focus the Name input when switching into editing a row
    useEffect(() => {
        if (editingId) {
            // Small delay to ensure the input is mounted
            const t = setTimeout(() => {
                nameInputRef.current?.focus?.();
            }, 0);
            return () => clearTimeout(t);
        }
    }, [editingId]);

    // Build hierarchical STT map (root: Roman; child: 1..n)
    const sttMap = useMemo(() => makeIndexMap(list), [list]);

    // Total score of leaf nodes
    const totalLeaf = useMemo(() => sumLeafScores(list), [list]);

    // Utility to build name with STT for saving
    const buildNameWithStt = (id: string | null, baseName: string): string => {
        const naked = stripGenericSttPrefix(baseName || '');
        if (!id) return naked; // fallback
        const label = sttMap[id];
        return label ? `${label}. ${naked}` : naked;
    };

    // Save handlers
    const saveEdit = async () => {
        if (!editingId) return;
        if (!formName.trim()) {
            message.error('Nhập tên tiêu chí');
            return;
        }
        if (formScore == null || isNaN(Number(formScore))) {
            message.error('Nhập điểm hợp lệ');
            return;
        }

        try {
            dispatch(setLoading(true));

            if (isNew && editParentId) {
                const parent = findNode(list, editParentId);
                if (!parent) {
                    message.error('Không tìm thấy tiêu chí cha');
                    return;
                }

                const childrenPayload = (parent.children || [])
                    .filter(c => c.id !== editingId)
                    .map(c => ({
                        id: c.id,
                        questionId: questionId,
                        // giữ nguyên tên hiện có của con (có thể đã có STT từ backend)
                        name: c.name,
                        description: c.description || '',
                        score: Number(c.score) || 0,
                        type: 2,
                        parentId: parent.id,
                    }));

                // Tạo tên có STT theo vị trí của bản ghi mới
                const newChildName = buildNameWithStt(editingId, formName.trim());

                childrenPayload.push({
                    id: undefined as any,
                    questionId: questionId,
                    name: newChildName,
                    description: formDesc || '',
                    score: Number(formScore) || 0,
                    type: 2,
                    parentId: parent.id,
                });

                const payloadParent: any = {
                    id: parent.id,
                    questionId,
                    // phụ huynh giữ nguyên tên cũ (đã có STT trong DB) vì không chỉnh sửa
                    name: parent.name,
                    description: parent.description || '',
                    score: Number(parent.score) || 0,
                    type: 2,
                    parentId: parent.parentId || null,
                    children: childrenPayload,
                };

                await $api.evaluation.saveDrl(payloadParent);
                message.success('Thêm tiêu chí con thành công');
            } else if (isNew && !editParentId) {
                // New root criterion
                const nameWithStt = buildNameWithStt(editingId, formName.trim());
                const payload: any = {
                    id: undefined,
                    questionId,
                    name: nameWithStt,
                    description: formDesc || '',
                    score: Number(formScore) || 0,
                    type: 2,
                    parentId: null,
                };
                await $api.evaluation.saveDrl(payload);
                message.success('Thêm tiêu chí thành công');
            } else {
                const rec = findNode(list, editingId);
                if (rec && rec.children && rec.children.length > 0) {
                    const childrenPayload = (rec.children || []).map(c => ({
                        id: c.id,
                        questionId: questionId,
                        name: c.name, // giữ nguyên tên con
                        description: c.description || '',
                        score: Number(c.score) || 0,
                        type: 2,
                        parentId: rec.id,
                    }));

                    const payloadParent: any = {
                        id: rec.id,
                        questionId,
                        // cập nhật tên cha với STT tương ứng
                        name: buildNameWithStt(rec.id, formName.trim()),
                        description: formDesc || '',
                        score: Number(formScore) || 0,
                        type: 2,
                        parentId: rec.parentId || null,
                        children: childrenPayload,
                    };
                    await $api.evaluation.saveDrl(payloadParent);
                } else {
                    const payload: any = {
                        id: editingId,
                        questionId,
                        name: buildNameWithStt(editingId, formName.trim()),
                        description: formDesc || '',
                        score: Number(formScore) || 0,
                        type: 2,
                        parentId: rec?.parentId || null,
                    };
                    await $api.evaluation.saveDrl(payload);
                }
                message.success('Cập nhật tiêu chí thành công');
            }

            resetEdit();
            await reload();
        } catch (e: any) {
            let errMsg;
            const type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Đã có lỗi xảy ra khi lưu';
            }
            message.error(errMsg);
        } finally {
            dispatch(setLoading(false));
        }
    };

    const handleDelete = async (id: string) => {
        try {
            dispatch(setLoading(true));
            await $api.evaluation.deleteDrl(id as any);
            message.success('Xóa tiêu chí thành công');
            await reload();
        } catch (e: any) {
            let errMsg;
            const type = typeof e?.response?.data?.errors?.exceptionMessage;
            if (type === 'string') {
                errMsg = e?.response?.data?.errors?.exceptionMessage;
            } else if (e?.response?.data?.errors?.exceptionMessage?.title) {
                errMsg = e?.response?.data?.errors?.exceptionMessage?.title;
            } else {
                errMsg = 'Đã có lỗi xảy ra khi xóa';
            }
            message.error(errMsg);
        } finally {
            dispatch(setLoading(false));
        }
    };

    // Columns
    const columns: any[] = [
        {
            title: 'STT',
            width: 90,
            dataIndex: 'id',
            align: 'center' as const,
            render: (_: any, record: EvaluationDto) => {
                // Hide STT only for new root row; show for new child so user sees last index
                if (editingId === record.id && isNew && !record.parentId) return '';
                const label = sttMap[record.id] || '';
                if (!label) return '';
                const isParent = !record.parentId;
                return isParent ? <span className='font-bold'>{label}</span> : <span>{label}</span>;
            },
        },
        {
            title: 'Tên tiêu chí',
            dataIndex: 'name',
            align: 'left' as const,
            render: (_: any, record: EvaluationDto) =>
                editingId === record.id ? (
                    <TextArea
                        ref={nameInputRef}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        value={formName}
                        onChange={e => setFormName(e.target.value)}
                        placeholder='Nhập tên tiêu chí'
                        onPressEnter={(e) => {
                            if (e.shiftKey) {
                                return; // allow default newline
                            }
                            e.preventDefault();
                            saveEdit();
                        }}
                    />
                ) : (
                    <div>
                        <div className={`${record.parentId ? '' : 'font-bold'} whitespace-pre-line break-words`}>
                            {stripSttByLabel(record.name, sttMap[record.id])}
                        </div>
                    </div>
                ),
        },
        {
            title: 'Điểm',
            width: 90,
            dataIndex: 'score',
            align: 'center' as const,
            render: (_: any, record: EvaluationDto) =>
                editingId === record.id ? (
                    <Input
                        type='number'
                        value={formScore}
                        onChange={e => setFormScore(Number(e.target.value))}
                        onPressEnter={(e) => { e.preventDefault(); saveEdit(); }}
                    />
                ) : (
                    <span>{record.score}</span>
                ),
        },
        {
            title: 'Mô tả',
            dataIndex: 'description',
            align: 'left' as const,
            render: (_: any, record: EvaluationDto) =>
                editingId === record.id ? (
                    <TextArea
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        value={formDesc}
                        onChange={e => setFormDesc(e.target.value)}
                        onPressEnter={(e) => {
                            if (e.shiftKey) {
                                return; // allow default newline
                            }
                            e.preventDefault();
                            saveEdit();
                        }}
                        placeholder='Mô tả (Shift+Enter để xuống dòng)'
                        style={{ maxWidth: 200 }}
                    />
                ) : (
                    <Typography.Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-line', maxWidth: 200, wordBreak: 'break-word' }}>
                        {record.description || ''}
                    </Typography.Paragraph>
                ),
        },
        {
            title: 'Thao tác',
            align: 'center' as const,
            width: 70,
            render: (_: any, record: EvaluationDto) => {
                const isEditing = editingId === record.id;
                const isChild = !!record.parentId;
                return (
                    <Space>
                        {isEditing ? (
                            <>
                                <Button type='primary' onClick={saveEdit}>Lưu</Button>
                                <Button onClick={cancelEdit}>Hủy</Button>
                            </>
                        ) : (
                            <>
                                {isChild ? null : (
                                    <Button onClick={() => startAddChild(record)} icon={<i className='fas fa-plus' />} ></Button>
                                )}
                                <Button onClick={() => startEdit(record)} icon={<i className='fas fa-edit' />}></Button>
                                <Popconfirm title='Xóa tiêu chí này?' onConfirm={() => handleDelete(record.id)}>
                                    <Button danger icon={<i className='fas fa-trash' />}></Button>
                                </Popconfirm>
                            </>
                        )}
                    </Space>
                );
            },
        },
    ];

    return (
        <Modal
            open={show}
            onCancel={() => { onHide(); }}
            title={
                <div>
                    <h1>Tiêu chí chấm điểm </h1>
                </div>
            }
            footer={[
                <Button key='close' onClick={onHide} danger>
                    Đóng
                </Button>
            ]}
            width={1000}
        >
            <div className="whitespace-pre-line my-3">
                <b>Câu hỏi:</b> {questionText ? `${questionText}` : ''}
            </div>

            {/* Duplicate section - Moved to top */}
            {subjectId && (
                <>
                    <div className='mb-2'>
                        <strong>Nhân bản tiêu chí từ câu hỏi khác</strong>
                    </div>
                    <div className='mb-3' style={{ display: 'flex', gap: 10 }}>
                        <Select
                            placeholder='Chọn bộ câu hỏi'
                            options={questionSuites.map(qs => ({ label: qs.name, value: qs.id }))}
                            value={selectedQuestionSuiteId}
                            onChange={val => setSelectedQuestionSuiteId(val)}
                            loading={loadingQuestionSuites}
                            style={{ flex: 1 }}
                        />
                        <Select
                            placeholder='Chọn câu hỏi nguồn'
                            value={selectedSourceQuestionId}
                            onChange={val => setSelectedSourceQuestionId(val)}
                            loading={loadingQuestions}
                            style={{ flex: 1, maxWidth: 450 }}
                            disabled={!selectedQuestionSuiteId || questions.length === 0}
                            notFoundContent={loadingQuestions ? 'Đang tải...' : 'Không có câu hỏi thực hành'}
                            showSearch
                            filterOption={(input, option: any) =>
                                (option?.label || option?.children)?.toString().toLowerCase().includes(input.toLowerCase())
                            }
                            optionLabelProp='label'
                        >
                            {questions.map(q => {
                                const fullText = q.questionText || '';
                                const shortText = fullText.length > 70 ? `${fullText.slice(0, 70)}...` : fullText;
                                return (
                                    <Select.Option key={q.id} value={q.id} label={shortText}>
                                        <Tooltip title={fullText} placement='topLeft'>
                                            <div
                                                style={{
                                                    maxWidth: '100%',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {fullText}
                                            </div>
                                        </Tooltip>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                        <Button
                            type='primary'
                            onClick={handleDuplicate}
                            loading={duplicating}
                            disabled={!selectedSourceQuestionId}
                        >
                            Nhân bản
                        </Button>
                    </div>
                    <Divider />
                </>
            )}

            <div className='mb-3' style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button type='primary' onClick={startAddRoot} icon={<i className='fas fa-plus' />}>Thêm tiêu chí</Button>
            </div>

            <Table
                rowKey='id'
                loading={loading}
                columns={columns}
                dataSource={list}
                pagination={false}
                expandable={{
                    expandedRowKeys,
                    onExpand: (expanded, record: any) => {
                        setExpandedRowKeys(prev => {
                            const set = new Set(prev as React.Key[]);
                            if (expanded) set.add(record.id);
                            else set.delete(record.id);
                            return Array.from(set);
                        });
                    },
                }}
                bordered
                size='middle'
                summary={() => (
                    <Table.Summary>
                        <Table.Summary.Row>
                            <Table.Summary.Cell index={0} />
                            <Table.Summary.Cell index={1}><strong>Tổng điểm</strong></Table.Summary.Cell>
                            <Table.Summary.Cell index={2}>
                                <div style={{ textAlign: 'center', fontWeight: 600 }}>{totalLeaf}</div>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell index={3} />
                            <Table.Summary.Cell index={4} />
                        </Table.Summary.Row>
                    </Table.Summary>
                )}
            />
        </Modal>
    );
};

export default PracticeQuestion__Evaluation;
